import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from '../../routes';
import { ROUTES } from '@repo/shared-types/routeRegistry';
import { authApi, type CompleteClerkOnboardingData, type TrustAccountingPlaidData } from '../../services/api/auth';
import { getClerkToken } from '../../services/api/clerkAuth';
import { useAppStore } from '../../store';
import { UserRole } from '@repo/shared-types';
import { getPostOnboardingRoute } from '../../utils/onboardingIdentity';
import { LoadingSpinner } from '../ui/LoadingSpinner';
import { getErrorWithAction, formatErrorMessage } from '@repo/shared-utils';
import { captureOnboardingEvent, ONBOARDING_ANALYTICS } from '../../analytics/onboardingFunnel';
import { PmOnboardingPlaidLink } from '../onboarding/PmOnboardingPlaidLink';
import { clearOnboardingWizardFormDrafts } from '../../utils/onboardingDraftStorage';
import {
  isPendingClerkOnboardingHandoffEnvelope,
  pendingPayloadNeedsPmPlaid,
  pendingStorageToCompletionPayload,
  PENDING_CLERK_ONBOARDING_STORAGE_VERSION,
} from '../../utils/pendingClerkOnboardingEnvelope';

/** Shorter wait in Vitest so tests do not hang on token polling. */
const CLERK_TOKEN_WAIT_MAX_MS = import.meta.env.MODE === 'test' || import.meta.env.VITEST ? 80 : 18_000;

async function delayForClerkPoll(ms: number): Promise<void> {
  if (!import.meta.env.VITEST) {
    await new Promise<void>((r) => setTimeout(r, ms));
    return;
  }
  /** `Date` may be stubbed in other tests; `performance.now()` stays monotonic for bounded waits. */
  const end = performance.now() + ms;
  while (performance.now() < end) {
    await new Promise<void>((r) => {
      const g = globalThis as typeof globalThis & { setImmediate?: (cb: () => void) => unknown };
      if (typeof g.setImmediate === 'function') {
        g.setImmediate(r);
      } else {
        setTimeout(r, 0);
      }
    });
  }
}

/**
 * Wait for Clerk token (e.g. after OAuth redirect). Staged backoff; returns whether a token is available.
 * Honors `isCancelled()` between iterations.
 */
async function waitForClerkToken(maxMs: number, isCancelled: () => boolean): Promise<boolean> {
  const start = performance.now();
  let delayMs = 200;
  const maxDelayMs = 2000;
  while (performance.now() - start < maxMs) {
    if (isCancelled()) return false;
    const token = await getClerkToken();
    if (token) return true;
    if (isCancelled()) return false;
    const remaining = maxMs - (performance.now() - start);
    if (remaining <= 0) break;
    await delayForClerkPoll(Math.min(delayMs, remaining));
    delayMs = Math.min(maxDelayMs, Math.floor(delayMs * 1.4));
  }
  if (isCancelled()) return false;
  return !!(await getClerkToken());
}

const PENDING_KEY = 'pendingClerkOnboarding';
const POST_SIGNUP_ONBOARDING_KEY = 'postSignUpNeedsOnboarding';

type ViewPhase =
  | { phase: 'loading' }
  | { phase: 'token_retry'; hasPendingPayload: boolean }
  | { phase: 'error'; message: string }
  | { phase: 'plaid_pm'; data: CompleteClerkOnboardingData }
  | { phase: 'completing' };

function mergePlaidPreferTokenized(data: CompleteClerkOnboardingData, plaid: TrustAccountingPlaidData): CompleteClerkOnboardingData {
  const { trustAccounting: _manual, ...rest } = data;
  return {
    ...rest,
    trustAccountingPlaid: plaid,
  };
}

async function persistHandoffFromLegacyIfNeeded(
  parsedRoot: unknown,
  full: CompleteClerkOnboardingData,
  isCancelled: () => boolean,
): Promise<{ parsedRoot: unknown; data: CompleteClerkOnboardingData }> {
  if (isPendingClerkOnboardingHandoffEnvelope(parsedRoot) || full.handoff_token?.trim()) {
    return { parsedRoot, data: full };
  }
  try {
    const { handoff_token } = await authApi.createClerkOnboardingHandoff(full as unknown as Record<string, unknown>);
    if (isCancelled()) return { parsedRoot, data: full };
    const requiresPmPlaid =
      full.role === 'PROPERTY_MANAGER' &&
      full.isSelfManagingOwner !== true &&
      !full.trustAccountingPlaid?.operating?.processorToken?.trim();
    const env = {
      v: PENDING_CLERK_ONBOARDING_STORAGE_VERSION,
      handoff_token: handoff_token.trim(),
      role: full.role,
      isSelfManagingOwner: full.isSelfManagingOwner,
      requires_pm_plaid: requiresPmPlaid,
    };
    sessionStorage.setItem(PENDING_KEY, JSON.stringify(env));
    return {
      parsedRoot: env,
      data: {
        role: full.role,
        isSelfManagingOwner: full.isSelfManagingOwner,
        handoff_token: handoff_token.trim(),
      },
    };
  } catch {
    return { parsedRoot, data: full };
  }
}

/**
 * Page Clerk redirects to after SignUp.
 * - If pending onboarding data exists (from Join flow): complete profile and redirect to app.
 * - If no data (user signed up via Sign up button): send to Join to select role and complete wizard.
 * - Property managers (non–self-managing) must link via Plaid (Clerk session required) before completion; skipping Plaid is not allowed.
 */
export const CompleteOnboardingView: React.FC = () => {
  const navigate = useNavigate();
  const [view, setView] = useState<ViewPhase>({ phase: 'loading' });
  const [bootstrapKey, setBootstrapKey] = useState(0);
  const [plaidHint, setPlaidHint] = useState<string | null>(null);
  const setAuthUser = useAppStore((s) => s.setAuthUser);
  const submittingRef = useRef(false);

  const submitPayload = useCallback(
    async (payload: CompleteClerkOnboardingData) => {
      if (submittingRef.current) return;
      submittingRef.current = true;
      setView({ phase: 'completing' });
      setPlaidHint(null);
      try {
        const user = await authApi.completeClerkOnboarding(payload);
        captureOnboardingEvent(ONBOARDING_ANALYTICS.ONBOARDING_COMPLETED, {
          role: payload.role,
          flow: 'standalone',
          stepId: 'complete_clerk',
          stepIndex: 0,
        });
        sessionStorage.removeItem(PENDING_KEY);
        clearOnboardingWizardFormDrafts();
        setAuthUser(user.id, user.role as UserRole, user.isSelfManagingOwner ?? false);
        const targetRoute = getPostOnboardingRoute(user.role, user.isSelfManagingOwner ?? false);
        navigate(targetRoute, { replace: true });
      } catch (err) {
        setView({ phase: 'error', message: formatErrorMessage(getErrorWithAction(err)) });
      } finally {
        submittingRef.current = false;
      }
    },
    [navigate, setAuthUser],
  );

  useEffect(() => {
    let cancelled = false;
    const isCancelled = () => cancelled;

    void (async () => {
      if (!isCancelled()) setView({ phase: 'loading' });

      const raw = sessionStorage.getItem(PENDING_KEY);

      if (!raw) {
        let tokenReady = await waitForClerkToken(CLERK_TOKEN_WAIT_MAX_MS, isCancelled);
        if (isCancelled()) return;
        if (!tokenReady) {
          tokenReady = !!(await getClerkToken());
        }
        if (isCancelled()) return;
        if (tokenReady) {
          const user = await authApi.getCurrentUserIfExists();
          if (isCancelled()) return;
          if (user) {
            setAuthUser(user.id, user.role as UserRole, user.isSelfManagingOwner ?? false);
            const targetRoute = getPostOnboardingRoute(user.role, user.isSelfManagingOwner ?? false);
            navigate(targetRoute, { replace: true });
            return;
          }
        }
        captureOnboardingEvent(ONBOARDING_ANALYTICS.CLERK_TOKEN_WAIT_EXHAUSTED, {
          role: 'unknown',
          flow: 'standalone',
          stepId: 'complete_clerk',
          stepIndex: 0,
          errorCode: 'clerk_token_wait_exhausted',
          extra: { has_pending_payload: false },
        });
        if (isCancelled()) return;
        setView({ phase: 'token_retry', hasPendingPayload: false });
        return;
      }

      let parsedRoot: unknown;
      try {
        parsedRoot = JSON.parse(raw) as unknown;
      } catch {
        sessionStorage.removeItem(PENDING_KEY);
        if (!isCancelled()) setView({ phase: 'error', message: 'Invalid onboarding data.' });
        return;
      }

      let data = pendingStorageToCompletionPayload(parsedRoot);
      if (!data) {
        sessionStorage.removeItem(PENDING_KEY);
        if (!isCancelled()) setView({ phase: 'error', message: 'Invalid onboarding data.' });
        return;
      }

      let tokenReady = await waitForClerkToken(CLERK_TOKEN_WAIT_MAX_MS, isCancelled);
      if (isCancelled()) return;
      if (!tokenReady) {
        tokenReady = !!(await getClerkToken());
      }
      if (isCancelled()) return;
      if (!tokenReady) {
        captureOnboardingEvent(ONBOARDING_ANALYTICS.CLERK_TOKEN_WAIT_EXHAUSTED, {
          role: data.role,
          flow: 'standalone',
          stepId: 'complete_clerk',
          stepIndex: 0,
          errorCode: 'clerk_token_wait_exhausted',
          extra: { has_pending_payload: true },
        });
        if (isCancelled()) return;
        setView({ phase: 'token_retry', hasPendingPayload: true });
        return;
      }

      const migrated = await persistHandoffFromLegacyIfNeeded(parsedRoot, data, isCancelled);
      parsedRoot = migrated.parsedRoot;
      data = migrated.data;

      if (pendingPayloadNeedsPmPlaid(parsedRoot, data)) {
        if (!isCancelled()) setView({ phase: 'plaid_pm', data });
        return;
      }

      await submitPayload(data);
    })();

    return () => {
      cancelled = true;
    };
  }, [bootstrapKey, navigate, setAuthUser, submitPayload]);

  const handleTokenRetry = useCallback(() => {
    setBootstrapKey((k) => k + 1);
  }, []);

  const handleSkipToJoin = useCallback(() => {
    sessionStorage.setItem(POST_SIGNUP_ONBOARDING_KEY, '1');
    navigate(ROUTES.ROOT + '?join=1', { replace: true });
  }, [navigate]);

  if (view.phase === 'token_retry') {
    return (
      <div className="min-h-screen bg-slate-900 flex flex-col items-center justify-center p-4 gap-4">
        <p className="text-slate-200 text-center max-w-md">
          Still signing you in. This can take a few seconds after Google or other providers. Try again, or continue to the join flow if
          you have not started onboarding yet.
        </p>
        <div className="flex flex-col gap-3 items-center sm:flex-row sm:flex-wrap sm:justify-center">
          <button
            type="button"
            onClick={handleTokenRetry}
            className="px-4 py-2 rounded-lg text-sm font-medium bg-sky-600 text-white hover:bg-sky-500 transition-colors"
          >
            Try again
          </button>
          {view.hasPendingPayload ? null : (
            <button
              type="button"
              onClick={handleSkipToJoin}
              className="px-4 py-2 rounded-lg text-sm font-medium border border-slate-600 text-slate-200 hover:bg-slate-800 transition-colors"
            >
              Continue to join
            </button>
          )}
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="text-sm text-sky-400 hover:text-sky-300 transition-colors"
          >
            Reload page
          </button>
        </div>
      </div>
    );
  }

  if (view.phase === 'error') {
    return (
      <div className="min-h-screen bg-slate-900 flex flex-col items-center justify-center p-4">
        <p className="text-red-400 mb-4">{view.message}</p>
        <div className="flex flex-col gap-3 items-center sm:flex-row sm:flex-wrap sm:justify-center">
          <button
            type="button"
            onClick={() => navigate(ROUTES.ROOT + '?join=1', { replace: true })}
            className="px-4 py-2 rounded-lg text-sm font-medium bg-sky-600 text-white hover:bg-sky-500 transition-colors"
          >
            Restart join flow
          </button>
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="text-sm text-sky-400 hover:text-sky-300 transition-colors"
          >
            Try again
          </button>
          <button
            type="button"
            onClick={() => navigate(ROUTES.ROOT)}
            className="text-sm text-slate-400 hover:text-white transition-colors"
          >
            Back to home
          </button>
        </div>
      </div>
    );
  }

  if (view.phase === 'plaid_pm') {
    return (
      <div className="min-h-screen bg-slate-900 flex flex-col items-center justify-center p-4 gap-6">
        <p className="text-slate-300 text-sm text-center max-w-md">
          Almost done. Link your operating account with Plaid to finish signup. We use tokenized bank connections only—no
          manual routing or account numbers are stored in the browser.
        </p>
        <PmOnboardingPlaidLink
          onLinked={(plaid) => void submitPayload(mergePlaidPreferTokenized(view.data, plaid))}
          onError={(m) => setPlaidHint(m)}
        />
        {plaidHint ? <p className="text-red-400 text-xs text-center max-w-md">{plaidHint}</p> : null}
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-900 flex flex-col items-center justify-center p-4">
      <LoadingSpinner size="lg" />
      <p className="text-slate-400 mt-4">
        {view.phase === 'completing' ? 'Completing your account…' : 'Completing your account...'}
      </p>
    </div>
  );
};
